/**
 * Términos y Privacidad — texto plano accesible desde el footer.
 * Texto base genérico ajustado a Colombia/LatAm. NO es asesoría legal —
 * para producto de revenue serio, consultar abogado.
 */
import type { Route } from "../App";

interface Props {
  go: (r: Route) => void;
  variant: "terms" | "privacy";
}

export function LegalScreen({ go, variant }: Props) {
  return (
    <div className="page" style={{ maxWidth: 760, margin: "0 auto" }}>
      <button className="btn btn-secondary btn-sm" onClick={() => go("dashboard")} style={{ marginBottom: 16 }}>
        ← Volver al inicio
      </button>
      {variant === "terms" ? <TermsContent /> : <PrivacyContent />}
    </div>
  );
}

function TermsContent() {
  return (
    <article className="legal-doc">
      <h1>Términos y Condiciones de Uso</h1>
      <p className="muted">Última actualización: 4 de mayo de 2026</p>

      <h2>1. ¿Quiénes somos?</h2>
      <p>
        PaniniNilabs es una plataforma operada por <b>Nilabs</b>, una iniciativa colombiana enfocada en
        soluciones de software con inteligencia artificial. Nuestro propósito es ofrecer un espacio
        gratuito y transparente para que coleccionistas del álbum oficial Panini FIFA Mundial 2026™
        intercambien sus láminas.
      </p>
      <p>
        <b>No estamos afiliados con Panini ni con FIFA</b>. Las marcas, escudos y nombres de jugadores
        son propiedad de sus respectivos titulares y se mencionan únicamente con fines descriptivos.
      </p>

      <h2>2. Cuenta y elegibilidad</h2>
      <p>
        Para usar la plataforma debes tener al menos 13 años (o la mayoría de edad legal en tu país
        de residencia). Eres responsable de la información que registras y de mantener la
        confidencialidad de tu contraseña.
      </p>

      <h2>3. Uso aceptable</h2>
      <ul>
        <li>No publiques contenido ilegal, ofensivo o que infrinja derechos de terceros.</li>
        <li>No intentes estafar a otros usuarios. Coordina entregas en lugares seguros.</li>
        <li>No uses bots o scripts para abusar del sistema de matches o mercado.</li>
        <li>El intercambio físico de láminas es responsabilidad de las partes — Nilabs no garantiza
          ni intermedia transacciones.</li>
      </ul>

      <h2>4. Mercado y dinero</h2>
      <p>
        El mercado permite ofertas tipo intercambio o venta directa. Nilabs no procesa pagos:
        cualquier coordinación de dinero (Nequi, Mercado Pago, efectivo) es entre los usuarios.
        No nos hacemos responsables de fraudes en pagos. Usa tu criterio y reporta usuarios
        sospechosos desde el botón 🚩.
      </p>

      <h2>5. Donaciones</h2>
      <p>
        Las donaciones a través de Mercado Pago o Nequi son voluntarias y no dan acceso a funciones
        adicionales. Sirven para cubrir costos de infraestructura.
      </p>

      <h2>6. Suspensión y eliminación</h2>
      <p>
        Podemos suspender o eliminar cuentas que violen estos términos, sean reportadas
        repetidamente, o muestren actividad fraudulenta. Puedes solicitar la eliminación de tu
        cuenta escribiendo a <a href="mailto:contacto@nilabs.co">contacto@nilabs.co</a>.
      </p>

      <h2>7. Cambios</h2>
      <p>
        Podemos actualizar estos términos. Si los cambios son sustanciales, te avisaremos por email
        o por una notificación en la app.
      </p>

      <h2>8. Contacto</h2>
      <p>
        Cualquier duda escríbenos a <a href="mailto:contacto@nilabs.co">contacto@nilabs.co</a>.
      </p>
    </article>
  );
}

function PrivacyContent() {
  return (
    <article className="legal-doc">
      <h1>Política de Privacidad</h1>
      <p className="muted">Última actualización: 4 de mayo de 2026</p>

      <h2>1. Información que recopilamos</h2>
      <ul>
        <li><b>Cuenta:</b> email, nombre, ciudad, país y zona/barrio (opcional).</li>
        <li><b>Perfil:</b> avatar, bio (opcional), inventario de láminas que marcas.</li>
        <li><b>Actividad:</b> propuestas de intercambio, mensajes de chat, reseñas, reportes.</li>
        <li><b>Técnica:</b> IP de la última conexión, navegador, dispositivo (para seguridad y métricas
          agregadas).</li>
        <li><b>Google Sign-In:</b> si te autenticas con Google, recibimos tu email y nombre. No
          tenemos acceso a tu contraseña ni a otros datos de tu cuenta Google.</li>
      </ul>

      <h2>2. Uso de la información</h2>
      <ul>
        <li>Hacer matching entre coleccionistas con láminas complementarias.</li>
        <li>Mostrar tu nombre, ciudad y reputación a otros usuarios cuando proponen intercambios.</li>
        <li>Enviar notificaciones in-app y por navegador (con tu permiso) sobre tratos y mensajes.</li>
        <li>Operar el servicio, prevenir fraude y mejorar la plataforma.</li>
      </ul>

      <h2>3. Quién puede ver qué</h2>
      <p>
        Tu email <b>NO</b> es visible para otros usuarios. Tu inventario de láminas es público
        para usuarios autenticados (saben qué tienes repetido y qué te falta — es la base del
        intercambio). Tu ciudad es visible. Las reseñas que recibes son públicas.
      </p>

      <h2>4. Compartir con terceros</h2>
      <p>
        No vendemos tus datos a terceros. Compartimos información solo con:
      </p>
      <ul>
        <li><b>Proveedores de infraestructura:</b> Hostinger (hosting) y Google (autenticación opcional).</li>
        <li><b>Autoridades:</b> si lo exige una orden judicial válida en Colombia.</li>
      </ul>

      <h2>5. Retención</h2>
      <p>
        Conservamos tus datos mientras tu cuenta esté activa. Si solicitas eliminar tu cuenta,
        borramos tu perfil, álbum y mensajes en un plazo de 30 días (los logs de auditoría se
        conservan hasta 1 año por motivos de seguridad).
      </p>

      <h2>6. Cookies y almacenamiento local</h2>
      <p>
        Usamos almacenamiento local del navegador para mantener tu sesión activa (tokens JWT) y
        guardar preferencias de UI. No usamos cookies de tracking publicitario.
      </p>

      <h2>7. Tus derechos</h2>
      <p>
        Tienes derecho a acceder, rectificar, eliminar o portar tus datos personales según la Ley
        1581 de 2012 (Habeas Data, Colombia). Para ejercer estos derechos escríbenos a
        <a href="mailto:contacto@nilabs.co"> contacto@nilabs.co</a>.
      </p>

      <h2>8. Menores de edad</h2>
      <p>
        El servicio está dirigido a personas de 13 años o más. Si descubrimos que un menor de esa
        edad creó cuenta sin consentimiento parental, la eliminaremos.
      </p>

      <h2>9. Contacto</h2>
      <p>
        Cualquier duda sobre privacidad: <a href="mailto:contacto@nilabs.co">contacto@nilabs.co</a>.
      </p>
    </article>
  );
}
